import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Switch,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateStatusPedido } from '../../../../services/api';
import DashboardFormFields from './DashboardFormFields';
import {
  getNome,
  toNum,
  calcTotalPedido,
  getPedidoComandaLabel,
  getPedidoMesaLabel,
  getCompId,
  menuPalette,
} from '../utils/dashboardHelpers';
import { DEFAULT_AVAILABILITY_RULE } from '../utils/cardapioAvailability';

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const FORM_TYPE_LABELS = {
  cardapio: { label: 'cardápio', newAction: 'Novo' },
  categoria: { label: 'categoria', newAction: 'Nova' },
  item: { label: 'item', newAction: 'Novo' },
};

const getFormDialogTitle = (formState = {}) => {
  const type = FORM_TYPE_LABELS[formState.type] || {
    label: String(formState.type || '').trim(),
    newAction: 'Novo',
  };
  return `${formState.data?.id ? 'Editar' : type.newAction} ${type.label}`.trim();
};

const DashboardModals = ({
  availabilityModal,
  setAvailabilityModal,
  closeAvailability,
  handleSaveAvailability,
  pedidoModal,
  pedidoModalData,
  closePedidoModal,
  allItems,
  allComplementos,
  pedidoAddItem,
  setPedidoAddItem,
  handleAddItemPedido,
  handleUpdateItemQtd,
  handleRemoveItemPedido,
  handleUpdateItemComplementos,
  openConfirmDialog,
  refreshPedidos,
  confirmDialog,
  closeConfirmDialog,
  handleConfirmDialogConfirm,
  formState,
  setFormState,
  closeForm,
  handleSaveForm,
  cardapios,
  allCategorias,
}) => (
  <>
          <Dialog open={availabilityModal.open} onClose={closeAvailability} maxWidth="md" fullWidth>
            <DialogTitle>Disponibilidade por cardápio</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {availabilityModal.cardapio ? getNome(availabilityModal.cardapio) : ''}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!availabilityModal.form.enabled}
                      onChange={(e) =>
                        setAvailabilityModal((s) => ({ ...s, form: { ...s.form, enabled: e.target.checked } }))
                      }
                    />
                  }
                  label="Liberar cardápios somente em dias/horários configurados"
                />
                {availabilityModal.form.enabled && (
                  <Stack spacing={2}>
                    <FormControl component="fieldset">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!availabilityModal.form.mostrarForaDoHorario}
                            onChange={(e) =>
                              setAvailabilityModal((s) => ({ ...s, form: { ...s.form, mostrarForaDoHorario: e.target.checked } }))
                            }
                          />
                        }
                        label="Fora do horário, exibir com aviso (desative para esconder)"
                      />
                    </FormControl>
                    {(availabilityModal.form.regras || []).map((regra, idx) => (
                      <Paper key={`regra-${idx}`} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle2" fontWeight={700}>Horário {idx + 1}</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {DAY_LABELS.map((diaLabel, diaIdx) => {
                              const isSelected = Array.isArray(regra.dias) && regra.dias.includes(diaIdx);
                              return (
                                <Chip
                                  key={diaLabel}
                                  label={diaLabel}
                                  color={isSelected ? 'primary' : 'default'}
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  onClick={() => {
                                    setAvailabilityModal((s) => {
                                      const regras = [...s.form.regras];
                                      const dias = Array.isArray(regra.dias) ? [...regra.dias] : [];
                                      if (isSelected) {
                                        regras[idx] = { ...regra, dias: dias.filter((d) => d !== diaIdx) };
                                      } else {
                                        regras[idx] = { ...regra, dias: [...dias, diaIdx].sort((a, b) => a - b) };
                                      }
                                      return { ...s, form: { ...s.form, regras } };
                                    });
                                  }}
                                  sx={{ minWidth: 56 }}
                                />
                              );
                            })}
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              label="Início"
                              type="time"
                              value={regra.inicio || ''}
                              onChange={(e) =>
                                setAvailabilityModal((s) => {
                                  const regras = [...s.form.regras];
                                  regras[idx] = { ...regra, inicio: e.target.value };
                                  return { ...s, form: { ...s.form, regras } };
                                })
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              label="Fim"
                              type="time"
                              value={regra.fim || ''}
                              onChange={(e) =>
                                setAvailabilityModal((s) => {
                                  const regras = [...s.form.regras];
                                  regras[idx] = { ...regra, fim: e.target.value };
                                  return { ...s, form: { ...s.form, regras } };
                                })
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Stack>
                          <Box textAlign="right">
                            <Button
                              size="small"
                              color="error"
                              onClick={() =>
                                setAvailabilityModal((s) => {
                                  const regras = (s.form.regras || []).filter((_, i) => i !== idx);
                                  return {
                                    ...s,
                                    form: {
                                      ...s.form,
                                      regras: regras.length
                                        ? regras
                                        : [{ ...DEFAULT_AVAILABILITY_RULE, dias: [...DEFAULT_AVAILABILITY_RULE.dias] }],
                                    },
                                  };
                                })
                              }
                            >
                              Remover horário
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setAvailabilityModal((s) => ({
                          ...s,
                          form: {
                            ...s.form,
                            regras: [
                              ...(s.form.regras || []),
                              { ...DEFAULT_AVAILABILITY_RULE, dias: [...DEFAULT_AVAILABILITY_RULE.dias] },
                            ],
                          },
                        }))
                      }
                    >
                      Adicionar Horário
                    </Button>
                  </Stack>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeAvailability}>Cancelar</Button>
              <Button variant="contained" onClick={handleSaveAvailability} disabled={!availabilityModal.cardapio}>Salvar</Button>
            </DialogActions>
          </Dialog>        

        <Dialog open={pedidoModal.open} onClose={closePedidoModal} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800, letterSpacing: '0.08em' }}>
            {pedidoModalData ? `Pedido #${pedidoModalData.id}` : 'Pedido'}
          </DialogTitle>
          <DialogContent dividers>
            {!pedidoModalData ? (
              <Typography variant="body2">Carregando pedido...</Typography>
            ) : (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                  <Typography fontWeight={700}>
                    Mesa {getPedidoMesaLabel(pedidoModalData)} · Comanda {getPedidoComandaLabel(pedidoModalData)} · Status {pedidoModalData.status}
                  </Typography>
                  <Typography color="text.secondary">
                    Total{' '}
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                      pedidoModalData.total ?? calcTotalPedido(pedidoModalData)
                    )}
                  </Typography>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, borderColor: menuPalette.border, order: 1 }}>
                  <Typography fontWeight={700} mb={1}>Itens da comanda</Typography>
                  {(pedidoModalData.itens || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nenhum item.</Typography>
                  ) : (
                    <List dense sx={{ py: 0 }}>
                      {(pedidoModalData.itens || []).map((it) => {
                        const qtd = it.quantidade || 1;
                        const compNames = (it.complementos || [])
                          .map((c) => c.complemento?.nome || c.nome)
                          .filter(Boolean);
                        const unit = toNum(it.preco_unit) || toNum(it.item?.preco);
                        const compTotal = (it.complementos || []).reduce(
                          (acc, c) => acc + (toNum(c.preco) || toNum(c.complemento?.preco)),
                          0
                        );
                        const lineTotal = (unit + compTotal) * (toNum(qtd) || 1);
                        return (
                          <ListItem key={it.id} sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary={`${qtd}x ${it.item?.nome || 'Item'}`}
                              secondary={compNames.length ? `Compl.: ${compNames.join(', ')}` : null}
                            />
                            <Typography fontWeight={800}>
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lineTotal)}
                            </Typography>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderColor: menuPalette.border, order: 3 }}>
                  <Typography fontWeight={700} mb={1}>Adicionar mais itens</Typography>
                  <Stack spacing={1.5}>
                    <Autocomplete
                      options={allItems}
                      getOptionLabel={(opt) => opt?.nome || `Item ${opt?.id}`}
                      renderOption={(props, opt) => (
                        <li {...props}>
                          <Box>
                            <Typography fontWeight={700}>{opt.nome || `Item ${opt.id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.cardapioNome ? `${opt.cardapioNome} · ${opt.categoriaNome || ''}` : opt.categoriaNome || ''}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      value={pedidoAddItem.item}
                      onChange={(_, val) => setPedidoAddItem((s) => ({ ...s, item: val || null }))}
                      renderInput={(params) => <TextField {...params} label="Item" placeholder="Selecione um item" />}
                    />
                    <Autocomplete
                      multiple
                      options={allComplementos}
                      getOptionLabel={(opt) => opt?.nome || `Complemento ${opt?.id}`}
                      value={allComplementos.filter((c) => (pedidoAddItem.comps || []).includes(c.id))}
                      onChange={(_, vals) => setPedidoAddItem((s) => ({ ...s, comps: vals.map((v) => v.id) }))}
                      renderInput={(params) => <TextField {...params} label="Complementos" placeholder="Selecione complementos (opcional)" />}
                    />
                    <Box textAlign="right">
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        disabled={!pedidoAddItem.item}
                        onClick={async () => {
                          if (!pedidoAddItem.item) return;
                          await handleAddItemPedido(pedidoModalData.id, pedidoAddItem.item, pedidoAddItem.comps || []);
                          setPedidoAddItem({ item: null, comps: [] });
                        }}
                      >
                        Adicionar item ao pedido
                      </Button>
                    </Box>
                  </Stack>
                </Paper>

                <Stack spacing={1} sx={{ order: 2 }}>
                  <Typography fontWeight={700}>Editar itens</Typography>
                  {(pedidoModalData.itens || []).map((it) => {
                    const compIds = (it.complementos || []).map((c) => getCompId(c)).filter(Boolean);
                    return (
                      <Paper key={it.id} variant="outlined" sx={{ p: 1.25, borderColor: menuPalette.border }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                          <Box>
                            <Typography fontWeight={700}>{it.item?.nome || 'Item'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(it.preco_unit || it.item?.preco || 0)}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleUpdateItemQtd(pedidoModalData.id, it.id, (it.quantidade || 1) - 1)}
                            >
                              -
                            </Button>
                            <Typography fontWeight={700}>{it.quantidade || 1}</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleUpdateItemQtd(pedidoModalData.id, it.id, (it.quantidade || 1) + 1)}
                            >
                              +
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() =>
                                openConfirmDialog({
                                  title: 'Remover item',
                                  message: 'Deseja remover este item do pedido?',
                                  confirmText: 'Remover',
                                  confirmColor: 'error',
                                  onConfirm: () => handleRemoveItemPedido(pedidoModalData.id, it.id),
                                })
                              }
                            >
                              Remover item
                            </Button>
                          </Stack>
                        </Stack>

                        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={1}>
                          {(it.complementos || []).map((c) => (
                            <Chip
                              key={getCompId(c)}
                              label={c.complemento?.nome || c.nome || `Comp ${getCompId(c)}`}
                              onDelete={() =>
                                handleUpdateItemComplementos(
                                  pedidoModalData.id,
                                  it.id,
                                  compIds.filter((id) => id !== getCompId(c))
                                )
                              }
                            />
                          ))}
                        </Stack>
                        <Autocomplete
                          multiple
                          options={allComplementos}
                          value={allComplementos.filter((c) => compIds.includes(c.id))}
                          onChange={(_, vals) =>
                            handleUpdateItemComplementos(
                              pedidoModalData.id,
                              it.id,
                              vals.map((v) => v.id)
                            )
                          }
                          getOptionLabel={(opt) => opt?.nome || `Complemento ${opt?.id}`}
                          renderInput={(params) => <TextField {...params} label="Complementos deste item" />}
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    );
                  })}
                </Stack>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closePedidoModal}>Fechar</Button>
            {pedidoModalData && (
              <Button
                color="error"
                onClick={() => {
                  openConfirmDialog({
                    title: 'Cancelar pedido',
                    message: 'Deseja cancelar este pedido? Essa ação não pode ser desfeita.',
                    confirmText: 'Cancelar pedido',
                    confirmColor: 'error',
                    onConfirm: async () => {
                      try {
                        await updateStatusPedido(pedidoModalData.id, 'cancelado');
                        await refreshPedidos();
                        toast.success('Pedido cancelado.');
                        closePedidoModal();
                      } catch (e) {
                        console.error('Erro ao cancelar pedido', e);
                        toast.error('Não foi possível cancelar o pedido.');
                      }
                    },
                  });
                }}
              >
                Cancelar pedido
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 800 }}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            {confirmDialog.message ? (
              typeof confirmDialog.message === 'string'
                ? <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{confirmDialog.message}</Typography>
                : confirmDialog.message
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmDialog}>Voltar</Button>
            <Button variant="contained" color={confirmDialog.confirmColor} onClick={handleConfirmDialogConfirm}>
              {confirmDialog.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={formState.open} onClose={closeForm} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800, letterSpacing: '0.08em' }}>
            {getFormDialogTitle(formState)}
          </DialogTitle>
          <DialogContent>{(
              <DashboardFormFields
                formState={formState}
                setFormState={setFormState}
                cardapios={cardapios}
                allCategorias={allCategorias}
                allComplementos={allComplementos}
              />
            )}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForm}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveForm}>Salvar</Button>
        </DialogActions>
      </Dialog>

  </>
);

export default DashboardModals;
